/**
 * Seeds Column Definitions
 *
 * Column definitions with JSX cell renderers.
 * Separated from columns.ts to isolate JSX dependencies.
 *
 * Pattern matches:
 * - src/sections/asset/config/columnDefinitions.tsx
 * - src/sections/customerManagement/config/columnDefinitions.tsx
 *
 * Column widths migrated to central registry (Day 2 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Phase 4 Update (2025-10-24):
 * - Added preseed column definitions import
 * - Updated getSeedColumns to support preseed type
 * - Updated getSeedColumnsConfig to support preseed type
 *
 * Rollback instructions (if needed):
 * 1. Revert this file to previous commit
 * 2. Central registry remains (no deletion needed)
 */

import { NameColumn } from '@/components/NameColumn';
import { OverflowText } from '@/components/OverflowText';
import { getColumnWidth } from '@/components/table/columns';
import { EmailCell } from '@/components/table/columns/cells';
import { ColumnPriority, Columns } from '@/components/table/types';
import { SeedStatusComponent } from '@/sections/seeds/components/badges';
import { TagsCell } from '@/sections/seeds/components/cells/TagsCell';
import { OriginCell } from '@/sections/seeds/components/OriginCell';
import { Seed } from '@/types';
import { formatRegistrar } from '@/utils/formatRegistrar.util';

import { SeedType } from './columns';
import {
  getPreseedColumns,
  getPreseedColumnsConfig,
} from './preseed/columnDefinitions';

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

/**
 * Get web application seed columns configuration
 */
const getWebappSeedColumns = (): Columns<Seed> => {
  return [
    {
      label: 'Status',
      id: 'status',
      className: 'capitalize',
      cell: (item: Seed) => {
        return <SeedStatusComponent seed={item} />;
      },
      ...getColumnWidth('seed', 'status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Asset',
      id: 'identifier',
      copy: true,
      cell: (seed: Seed) => {
        return (
          <div className="flex flex-col">
            <OverflowText
              text={seed.identifier}
              className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary font-bold"
            />
            {seed.group && seed.group !== seed.identifier && (
              <span className="text-sm">{seed.group}</span>
            )}
          </div>
        );
      },
      ...getColumnWidth('seed', 'identifier'),
      // Flexible absorber column: route through applyColumnWidth's expandable
      // branch so its <col> becomes width:auto in non-responsive mode and soaks
      // up leftover viewport width (keeps fixed-width columns at their true spec).
      isExpandable: { minWidth: 220 },
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Scan Profile',
      id: 'profile',
      cell: (seed: Seed) => seed.profile || 'Default',
      ...getColumnWidth('seed', 'profile'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Tags',
      id: 'tags',
      cell: (seed: Seed) => <TagsCell seed={seed} />,
      ...getColumnWidth('seed', 'tags'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Origin',
      id: 'origin',
      cell: (item: Seed) => <OriginCell seed={item} />,
      ...getColumnWidth('seed', 'origin'),
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      label: 'First Seen',
      id: 'created',
      ...getColumnWidth('seed', 'created'),
      copy: true,
      cell: 'date',
      sorting: true,
      responsive: {
        priority: ColumnPriority.MEDIUM,
        minViewportWidth: 1024,
      },
    },
    {
      label: 'Last Seen',
      id: 'visited',
      ...getColumnWidth('seed', 'visited'),
      copy: true,
      cell: 'date',
      sorting: true,
      responsive: {
        priority: ColumnPriority.HIGH,
        minViewportWidth: 900,
      },
    },
  ];
};

/**
 * Get domain seed columns configuration
 */
const getDomainSeedColumns = (): Columns<Seed> => {
  return [
    {
      label: 'Status',
      id: 'status',
      className: 'capitalize',
      cell: (item: Seed) => {
        return <SeedStatusComponent seed={item} />;
      },
      ...getColumnWidth('seed', 'status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Domain/FQDN',
      id: 'identifier',
      copy: true,
      cell: (seed: Seed) => {
        return (
          <OverflowText
            text={seed.identifier}
            className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary font-bold"
          />
        );
      },
      ...getColumnWidth('seed', 'identifier'),
      // Flexible absorber column: route through applyColumnWidth's expandable
      // branch so its <col> becomes width:auto in non-responsive mode and soaks
      // up leftover viewport width (keeps fixed-width columns at their true spec).
      isExpandable: { minWidth: 220 },
    },
    {
      label: 'Registrar',
      id: 'registrar',
      cell: item => {
        const registrar = formatRegistrar(item.registrar);
        return registrar ? <OverflowText text={registrar} /> : '-';
      },
      ...getColumnWidth('seed', 'registrar'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Registrant Email',
      id: 'email',
      cell: item => <EmailCell entity={item} field="email" />,
      ...getColumnWidth('seed', 'email'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Registrant Organization',
      id: 'registrant',
      cell: item =>
        item.registrant ? <OverflowText text={item.registrant} /> : '-',
      ...getColumnWidth('seed', 'registrant'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Scan Profile',
      id: 'profile',
      cell: (seed: Seed) => seed.profile || 'Default',
      ...getColumnWidth('seed', 'profile'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Tags',
      id: 'tags',
      cell: (seed: Seed) => <TagsCell seed={seed} />,
      ...getColumnWidth('seed', 'tags'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Origin',
      id: 'origin',
      cell: (item: Seed) => <OriginCell seed={item} />,
      ...getColumnWidth('seed', 'origin'),
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      label: 'First Seen',
      id: 'created',
      ...getColumnWidth('seed', 'created'),
      copy: true,
      cell: 'date',
      sorting: true,
      responsive: {
        priority: ColumnPriority.MEDIUM,
        minViewportWidth: 1024,
      },
    },
    {
      label: 'Last Seen',
      id: 'visited',
      ...getColumnWidth('seed', 'visited'),
      copy: true,
      cell: 'date',
      sorting: true,
      responsive: {
        priority: ColumnPriority.HIGH,
        minViewportWidth: 900,
      },
    },
  ];
};

/**
 * Get IP/CIDR seed columns configuration
 */
const getIpSeedColumns = (): Columns<Seed> => {
  return [
    {
      label: 'Status',
      id: 'status',
      className: 'capitalize',
      cell: (item: Seed) => {
        return <SeedStatusComponent seed={item} />;
      },
      ...getColumnWidth('seed', 'status'),
    },
    {
      label: 'CIDR Range/IP Address',
      copy: true,
      id: 'identifier',
      cell: (seed: Seed) => <NameColumn name={seed.identifier} />,
      ...getColumnWidth('seed', 'identifier'),
      // Flexible absorber column: route through applyColumnWidth's expandable
      // branch so its <col> becomes width:auto in non-responsive mode and soaks
      // up leftover viewport width (keeps fixed-width columns at their true spec).
      isExpandable: { minWidth: 220 },
    },
    {
      label: 'Registrant Organization',
      id: 'registrant',
      cell: item =>
        item.registrant ? <OverflowText text={item.registrant} /> : '-',
      ...getColumnWidth('seed', 'registrant'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Customer',
      id: 'customer',
      cell: item =>
        item.customer ? <OverflowText text={item.customer} /> : '-',
      ...getColumnWidth('seed', 'customer'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Internet Service Provider',
      id: 'registrar',
      cell: item => {
        const registrar = formatRegistrar(item.registrar);
        return registrar ? <OverflowText text={registrar} /> : '-';
      },
      ...getColumnWidth('seed', 'registrar'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Country',
      id: 'country',
      cell: (item: Seed) =>
        item.country ? <OverflowText text={item.country} /> : '-',
      ...getColumnWidth('seed', 'country'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Scan Profile',
      id: 'profile',
      cell: (seed: Seed) => seed.profile || 'Default',
      ...getColumnWidth('seed', 'profile'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Tags',
      id: 'tags',
      cell: (seed: Seed) => <TagsCell seed={seed} />,
      ...getColumnWidth('seed', 'tags'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Origin',
      id: 'origin',
      cell: (item: Seed) => <OriginCell seed={item} />,
      ...getColumnWidth('seed', 'origin'),
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      label: 'First Seen',
      id: 'created',
      ...getColumnWidth('seed', 'created'),
      copy: true,
      cell: 'date',
      sorting: true,
      responsive: {
        priority: ColumnPriority.MEDIUM,
        minViewportWidth: 1024,
      },
    },
    {
      label: 'Last Seen',
      id: 'visited',
      ...getColumnWidth('seed', 'visited'),
      copy: true,
      cell: 'date',
      sorting: true,
      responsive: {
        priority: ColumnPriority.HIGH,
        minViewportWidth: 900,
      },
    },
  ];
};

/**
 * Get type-specific seed columns
 *
 * Updated in Phase 4 to support preseed type.
 *
 * @param type - Seed type (domain, ip, webapplication, preseed)
 * @returns Column configuration for the seed type
 *
 * @example
 * ```tsx
 * const columns = getSeedColumns('domain');
 * const preseedColumns = getSeedColumns('preseed');
 * ```
 */
export const getSeedColumns = (type: SeedType): Columns<Seed> => {
  switch (type) {
    case 'webapplication':
      return getWebappSeedColumns();
    case 'domain':
      return getDomainSeedColumns();
    case 'ip':
      return getIpSeedColumns();
    case 'preseed':
      return getPreseedColumns() as unknown as Columns<Seed>;
    default:
      // Fallback for unknown types (should never happen with proper typing)
      return getIpSeedColumns();
  }
};

/**
 * Get sortable column configuration for seed type
 *
 * Updated in Phase 4 to support preseed type with its own configuration.
 *
 * @param type - Seed type (domain, ip, webapplication, preseed)
 * @returns Sortable column configuration with default and disabled columns
 *
 * @example
 * ```tsx
 * const config = getSeedColumnsConfig('domain');
 * const preseedConfig = getSeedColumnsConfig('preseed');
 * ```
 */
export const getSeedColumnsConfig = (type: SeedType) => {
  // Preseed has its own column configuration
  if (type === 'preseed') {
    return getPreseedColumnsConfig();
  }

  const columns = getSeedColumns(type);

  // Columns that live in the picker but are hidden by default (opt-in).
  // Registrar is shown by default on the Domains tab, but stays opt-in on the
  // IP/CIDR tab — there the same column is labeled "Internet Service Provider".
  const optInColumnIds =
    type === 'domain'
      ? ['tags', 'profile']
      : ['registrar', 'tags', 'profile', 'country'];

  // Active (default-visible) columns are everything that isn't opt-in.
  // Timestamp columns (created, visited) are regular columns that can be hidden/reordered
  const activeColumns = columns.filter(col => !optInColumnIds.includes(col.id));

  // Walk optInColumnIds (not the column list) so the picker order stays stable
  const availableColumns = [];
  for (const id of optInColumnIds) {
    const col = columns.find(c => c.id === id);
    if (col) {
      availableColumns.push({
        id: col.id,
        label: col.label,
        disabled: false,
        isDefault: false,
      });
    }
  }

  return {
    fixed: [],
    active: activeColumns.map(({ id, label }) => ({
      id,
      label,
      disabled: id === 'identifier', // Only identifier is required
      isDefault: id === 'identifier',
    })),
    available: availableColumns,
  };
};
